import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Link, 
  Container,
  Alert,
  InputAdornment,
  useTheme
} from '@mui/material';
import { 
  Email as EmailIcon, 
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { createCompanyV2 } from '../utils/companyMembership';

export default function Signup() {
  const { signup } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyLicense, setCompanyLicense] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate inputs
    if (!email || !email.trim()) {
      setError('Email is required.');
      return;
    }
    
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    
    // V2: Always require company name - invite code logic will be added later
    if (!companyName || !companyName.trim()) {
      setError('Company name is required. This will be your company name in Site Sync.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    let user = null; // Track user for cleanup if needed
    
    try {
      console.log('[signup] Starting signup process...');
      console.log('[signup] Email:', email);
      console.log('[signup] Company name:', companyName);
      
      // Step 1: Create Firebase Auth user
      console.log('[signup] Step 1: Creating Firebase Auth user...');
      const userCredential = await signup(email, password);
      user = userCredential.user;
      console.log('[signup] ✅ Firebase Auth user created:', user.uid);
      
      // Step 2: Prepare user name
      const name = displayName || email.split('@')[0];
      console.log('[signup] Step 2: User name:', name);
      
      // Step 3: Create company + admin user (V2 simplified flow)
      console.log('[signup] Step 3: Creating company and setting up admin user...');
      let companyId;
      
      try {
        // Build company profile data from form
        const companyProfileData = {
          name: companyName.trim(),
          phone: companyPhone.trim() || undefined,
          address: companyAddress.trim() || undefined,
          email: companyEmail.trim() || email, // Use user email as fallback
          website: companyWebsite.trim() || undefined,
          licenseNumber: companyLicense.trim() || undefined,
        };
        
        companyId = await createCompanyV2(
          user.uid,
          email,
          name,
          companyProfileData
        );
        
        if (!companyId) {
          throw new Error('Company creation returned no company ID');
        }
        
        console.log('[signup] ✅ Company and admin user created:', companyId);
      } catch (companyError) {
        console.error('[signup] ❌ Failed to create company:', companyError);
        
        // Clean up auth user if company creation fails
        if (user) {
          try {
            console.log('[signup] Cleaning up auth user...');
            await user.delete();
            console.log('[signup] ✅ Auth user cleaned up');
          } catch (deleteError) {
            console.error('[signup] ❌ Failed to clean up auth user:', deleteError);
          }
        }
        
        throw new Error(`Failed to create company: ${companyError.message}. Please try again.`);
      }
      
      // Step 4: Navigate to onboarding
      console.log('[signup] ✅ Signup completed successfully!');
      console.log('[signup] Summary:', {
        userId: user.uid,
        companyId: companyId,
        email: email,
        role: 'admin'
      });
      
      setLoading(false);
      navigate('/onboarding');
    } catch (err) {
      console.error('❌ Signup error:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        name: err.name,
        stack: err.stack
      });
      
      // Show detailed error message
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.code) {
        errorMessage = `Error (${err.code}): ${err.message || 'Unknown error'}`;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}08 100%)`,
        py: { xs: 4, sm: 6 },
        overflowY: 'auto'
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ mb: 2, color: 'text.secondary' }}
          >
            Back to Home
          </Button>
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ 
              color: 'primary.main',
              fontSize: { xs: '1.75rem', sm: '2.125rem' }
            }}
          >
            Site Sync
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create your account to get started.
          </Typography>
        </Box>

        <Paper 
          elevation={8}
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}
        >
          <Typography 
            variant="h5" 
            component="h2"
            fontWeight="bold"
            gutterBottom 
            align="center"
            sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
          >
            Sign Up
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError('')}
              action={
                <Button color="inherit" size="small" onClick={() => setError('')}>
                  Dismiss
                </Button>
              }
            >
              <Typography variant="body2" component="div">
                <strong>Signup Failed:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                Please check the browser console for more details.
              </Typography>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Your Account
            </Typography>
            
            <TextField
              label="Your Name"
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              fullWidth
              autoComplete="name"
              placeholder="Your Full Name"
              sx={{ mb: 2.5 }}
              helperText="Optional - defaults to your email if not provided"
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              required
              autoComplete="email"
              sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              required
              autoComplete="new-password"
              helperText="Choose a strong password (at least 6 characters)"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
              Company Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You'll be set as the admin of your company. You can add team members later.
            </Typography>
            
            <TextField
              label="Company Name"
              type="text"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              fullWidth
              required
              autoComplete="organization"
              placeholder="Your Company Name"
              sx={{ mb: 2.5 }}
              error={error && error.includes('Company name')}
            />
            
            <TextField
              label="Company Phone"
              type="tel"
              value={companyPhone}
              onChange={e => setCompanyPhone(e.target.value)}
              fullWidth
              autoComplete="tel"
              placeholder="(555) 123-4567"
              sx={{ mb: 2.5 }}
              helperText="Optional - can be updated later"
            />
            
            <TextField
              label="Company Address"
              type="text"
              value={companyAddress}
              onChange={e => setCompanyAddress(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="123 Main St, City, State 12345"
              sx={{ mb: 2.5 }}
              helperText="Optional - can be updated later"
            />
            
            <TextField
              label="Company Email"
              type="email"
              value={companyEmail}
              onChange={e => setCompanyEmail(e.target.value)}
              fullWidth
              autoComplete="email"
              placeholder="info@yourcompany.com"
              sx={{ mb: 2.5 }}
              helperText="Optional - defaults to your account email"
            />
            
            <TextField
              label="Company Website"
              type="url"
              value={companyWebsite}
              onChange={e => setCompanyWebsite(e.target.value)}
              fullWidth
              autoComplete="url"
              placeholder="https://www.yourcompany.com"
              sx={{ mb: 2.5 }}
              helperText="Optional - can be updated later"
            />
            
            <TextField
              label="License Number"
              type="text"
              value={companyLicense}
              onChange={e => setCompanyLicense(e.target.value)}
              fullWidth
              placeholder="Your contractor/business license number"
              sx={{ mb: 2.5 }}
              helperText="Optional - your contractor/business license number"
            />
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth 
              disabled={loading}
              size="large"
              sx={{ 
                mb: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link 
                component={RouterLink} 
                to="/login"
                sx={{ 
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Log In
              </Link>
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            By signing up, you agree to our Terms of Service and Privacy Policy.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
} 